import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui-kit";
import { Star, X, CheckCircle } from "@phosphor-icons/react";

export default function RatingModal({ open, onClose, rideId, targetName = "", targetRole = "chauffeur", onDone }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    if (open) {
      setRating(0); setHover(0); setComment(""); setDone(false);
    }
  }, [open]);

  const submit = async () => {
    if (!rating) return toast.error("Choisissez une note");
    setSubmitting(true);
    try {
      await api.post(`/rides/${rideId}/rate`, { rating, comment: comment || null });
      setDone(true);
      toast.success("Merci pour votre évaluation");
      if (onDone) onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[950] bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: 300, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 300, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="fixed inset-x-0 bottom-0 z-[960] mx-auto max-w-[540px] px-2 pb-2"
            data-testid="rating-sheet"
          >
            <div className="max-h-[82dvh] overflow-y-auto rounded-3xl glass-strong p-4 glow-blue min-[390px]:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-widest text-primary">Évaluation</div>
                  <h2 className="font-heading text-2xl font-bold">Noter {targetRole}</h2>
                </div>
                <button onClick={onClose} className="rounded-full bg-white/10 p-2 press-down" data-testid="rating-close">
                  <X size={18} weight="bold" />
                </button>
              </div>
              {done ? (
                <div className="py-6 text-center">
                  <CheckCircle size={48} weight="duotone" className="mx-auto text-secondary" />
                  <div className="mt-2 font-heading text-xl font-bold">Merci !</div>
                  <Button variant="primary" className="mt-4 w-full" onClick={onClose} data-testid="rating-done-close">Fermer</Button>
                </div>
              ) : (
                <>
                  {targetName && <div className="mt-1 text-sm text-muted-foreground">Votre {targetRole}: <span className="font-bold text-foreground">{targetName}</span></div>}
                  <div className="mt-4 flex items-center justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((n) => {
                      const active = n <= (hover || rating);
                      return (
                        <button
                          key={n}
                          onMouseEnter={() => setHover(n)}
                          onMouseLeave={() => setHover(0)}
                          onClick={() => setRating(n)}
                          className="press-down"
                          data-testid={`star-${n}`}
                        >
                          <Star size={44} weight={active ? "fill" : "regular"} className={active ? "text-primary" : "text-white/30"} />
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Commentaire (optionnel)…"
                    rows={3}
                    className="mt-4 w-full resize-none rounded-xl border border-white/10 bg-white/5 p-3 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                    data-testid="rating-comment"
                  />
                  <Button variant="primary" className="mt-3 w-full py-4" onClick={submit} disabled={submitting || !rating} data-testid="rating-submit">
                    {submitting ? "Envoi..." : "Envoyer l'évaluation"}
                  </Button>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
